/**
 * useHeatingCostConfig — Configuration Hook
 *
 * Manages fuel prices and investment costs with localStorage persistence.
 * Provides methods to update individual prices/costs and reset to defaults.
 */

import { useState, useCallback, useMemo } from 'react';
import type { FuelPrice, InvestmentCostDefinition, HeatingCostConfig, FuelVariantId } from '../types/heatingCost.types';
import { getDefaultFuelPrices } from '../constants/fuelPrices';
import { getDefaultInvestmentCosts } from '../constants/investmentCosts';

export const STORAGE_KEY_PRICES = 'ec-heating-cost-prices';
export const STORAGE_KEY_INVESTMENT = 'ec-heating-cost-investment';
export const STORAGE_KEY_SETTINGS = 'ec-heating-cost-settings';

export interface HeatingCostSettings {
  discountRate: number;
  fuelPriceGrowthRate: number;
  analysisYears: number;
}

export const DEFAULT_SETTINGS: HeatingCostSettings = {
  discountRate: 0.05,
  fuelPriceGrowthRate: 0.03,
  analysisYears: 20,
};

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch {
    // Invalid JSON or no localStorage — use fallback
  }
  return fallback;
}

function saveToStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

export function useHeatingCostConfig(): {
  config: HeatingCostConfig;
  updateFuelPrice: (variantId: FuelVariantId, pricePerUnit: number) => void;
  updateInvestmentCost: (variantId: FuelVariantId, updates: Partial<Pick<InvestmentCostDefinition, 'installationCost' | 'annualMaintenanceCost'>>) => void;
  updateSettings: (updates: Partial<HeatingCostSettings>) => void;
  resetPrices: () => void;
  resetInvestmentCosts: () => void;
  resetAll: () => void;
} {
  const [fuelPrices, setFuelPrices] = useState<FuelPrice[]>(() =>
    loadFromStorage(STORAGE_KEY_PRICES, getDefaultFuelPrices())
  );

  const [investmentCosts, setInvestmentCosts] = useState<InvestmentCostDefinition[]>(() =>
    loadFromStorage(STORAGE_KEY_INVESTMENT, getDefaultInvestmentCosts())
  );

  const [settings, setSettings] = useState<HeatingCostSettings>(() =>
    loadFromStorage(STORAGE_KEY_SETTINGS, DEFAULT_SETTINGS)
  );

  const updateFuelPrice = useCallback((variantId: FuelVariantId, pricePerUnit: number) => {
    setFuelPrices(prev => {
      const updated = prev.map(p =>
        p.variantId === variantId
          ? { ...p, pricePerUnit, isUserModified: true }
          : p
      );
      saveToStorage(STORAGE_KEY_PRICES, updated);
      return updated;
    });
  }, []);

  const updateInvestmentCost = useCallback((
    variantId: FuelVariantId,
    updates: Partial<Pick<InvestmentCostDefinition, 'installationCost' | 'annualMaintenanceCost'>>
  ) => {
    setInvestmentCosts(prev => {
      const updated = prev.map(c =>
        c.variantId === variantId
          ? { ...c, ...updates, isUserModified: true }
          : c
      );
      saveToStorage(STORAGE_KEY_INVESTMENT, updated);
      return updated;
    });
  }, []);

  const updateSettings = useCallback((updates: Partial<HeatingCostSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...updates };
      saveToStorage(STORAGE_KEY_SETTINGS, updated);
      return updated;
    });
  }, []);

  const resetPrices = useCallback(() => {
    const defaults = getDefaultFuelPrices();
    setFuelPrices(defaults);
    saveToStorage(STORAGE_KEY_PRICES, defaults);
  }, []);

  const resetInvestmentCosts = useCallback(() => {
    const defaults = getDefaultInvestmentCosts();
    setInvestmentCosts(defaults);
    saveToStorage(STORAGE_KEY_INVESTMENT, defaults);
  }, []);

  const resetAll = useCallback(() => {
    resetPrices();
    resetInvestmentCosts();
    setSettings(DEFAULT_SETTINGS);
    saveToStorage(STORAGE_KEY_SETTINGS, DEFAULT_SETTINGS);
  }, [resetPrices, resetInvestmentCosts]);

  const config: HeatingCostConfig = useMemo(() => ({
    fuelPrices,
    investmentCosts,
    ...settings,
  }), [fuelPrices, investmentCosts, settings]);

  return {
    config,
    updateFuelPrice,
    updateInvestmentCost,
    updateSettings,
    resetPrices,
    resetInvestmentCosts,
    resetAll,
  };
}
